/**
 * Annex-B H.264 demuxing — the byte-level half of the video path, kept free of DOM globals so it
 * can be tested. Ported from the working viewer; every rule here was learned from a real failure.
 */

/** NAL unit type. `nal` includes its leading start code (00 00 01 or 00 00 00 01). */
export function nalType(nal: Uint8Array): number {
  let p = 0
  while (p < nal.length && nal[p] === 0) p++
  if (nal[p] === 1) p++
  return (nal[p] ?? 0) & 0x1f
}

/** `avc1.PPCCLL` codec string built from the SPS — WebCodecs needs it to configure the decoder. */
export function codecFromSps(sps: Uint8Array): string {
  let p = 0
  while (p < sps.length && sps[p] === 0) p++
  if (sps[p] === 1) p++
  p++ // skip the NAL header byte (0x67)
  const hx = (n: number) => (n ?? 0).toString(16).padStart(2, '0')
  return 'avc1.' + hx(sps[p] ?? 0) + hx(sps[p + 1] ?? 0) + hx(sps[p + 2] ?? 0)
}

/** Index of the next start code (00 00 01) at or after `from`, or -1. */
export function findStartCode(b: Uint8Array, from: number): number {
  for (let i = from; i + 2 < b.length; i++) {
    if (b[i] === 0 && b[i + 1] === 0 && b[i + 2] === 1) return i
  }
  return -1
}

export interface AccessUnit {
  /** NALs of one frame, each still carrying its start code (concatenate to feed the decoder). */
  nals: Uint8Array[]
  /** IDR present → this is a keyframe. */
  key: boolean
  /** SPS of this access unit, when it carries one (needed once, to configure the decoder). */
  sps?: Uint8Array
}

/** Concatenate an access unit's NALs into the single buffer WebCodecs expects. */
export function joinAccessUnit(au: AccessUnit): Uint8Array {
  const total = au.nals.reduce((n, x) => n + x.length, 0)
  const data = new Uint8Array(total)
  let o = 0
  for (const x of au.nals) {
    data.set(x, o)
    o += x.length
  }
  return data
}

/**
 * Splits a byte stream into access units.
 *
 * - A NAL is only emitted once the **next** start code proves it complete; the tail stays buffered.
 *   Feeding a half NAL to the decoder corrupts everything until the next keyframe, and scrcpy can
 *   go seconds between keyframes (one old Huawei sent exactly one in six).
 * - NAL type 12 (filler) is dropped: the server sends those as an idle keepalive.
 * - A VCL NAL (types 1..5) closes the access unit — scrcpy emits one slice per frame.
 */
export class AnnexBDemuxer {
  private buf = new Uint8Array(0)
  private nals: Uint8Array[] = []

  push(chunk: Uint8Array): AccessUnit[] {
    const merged = new Uint8Array(this.buf.length + chunk.length)
    merged.set(this.buf)
    merged.set(chunk, this.buf.length)
    this.buf = merged

    const out: AccessUnit[] = []
    let start = findStartCode(this.buf, 0)
    if (start < 0) return out
    let next: number
    while ((next = findStartCode(this.buf, start + 3)) >= 0) {
      // a start code is 3 or 4 bytes: if a zero precedes it, that zero belongs to the start code
      const end = this.buf[next - 1] === 0 ? next - 1 : next
      const nal = this.buf.slice(start, end)
      const t = nalType(nal)
      if (t !== 12) {
        this.nals.push(nal)
        if (t >= 1 && t <= 5) {
          const types = this.nals.map(nalType)
          const spsIdx = types.indexOf(7)
          out.push({
            nals: this.nals,
            key: types.includes(5),
            ...(spsIdx >= 0 ? { sps: this.nals[spsIdx]! } : {}),
          })
          this.nals = []
        }
      }
      start = end
    }
    this.buf = this.buf.slice(start)
    return out
  }
}
